import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { ApplyNowForm } from "./ApplyNowForm";
import { CheckCircle, FileText, Users, Package, DollarSign, ShieldCheck, Zap, HeadphonesIcon, Monitor } from "lucide-react";

interface DealershipPageProps {
  setCurrentPage: (page: string) => void;
}

export function DealershipPage({ setCurrentPage }: DealershipPageProps) {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#e6f2ff] via-white to-gray-50 pt-16 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="text-lg md:text-xl text-[#004aad] mb-4 tracking-wide">
              MusliFin, Your Muslim Financing Partner
            </div>
            <h1 className="text-4xl md:text-6xl mb-6 text-gray-900">
              Become a MusliFin Partner Dealership
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Offer Shariah-compliant auto finance to your customers — no added complexity.
            </p>
            <div className="flex justify-center">
              <ApplyNowForm 
                asModal={true}
                defaultInterest="Becoming a Partner"
                defaultMessage="I'm interested in becoming a partner with MusliFin and would like to learn more about partnership opportunities, requirements, and how we can work together to provide Shariah-compliant auto financing solutions."
                requirePhoneNumber={true}
                triggerButton={
                  <Button 
                    size="lg" 
                    className="bg-[#004aad] hover:bg-[#003a8a] text-white px-8 py-4 text-lg"
                  >
                    Apply Now
                  </Button>
                }
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works (Dealerships) Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl mb-6 text-gray-900">How It Works for Dealerships</h2>
            <p className="text-xl text-gray-600">Four simple steps to partner with MusliFin</p>
          </div>
          
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8">
            {/* Step 1 */}
            <Card className="p-8 text-center border-2 border-[#004aad] relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-[#004aad] rounded-full flex items-center justify-center">
                <span className="text-white text-sm">1</span>
              </div>
              <div className="w-16 h-16 bg-[#ccdfff] rounded-full flex items-center justify-center mx-auto mb-6 mt-4">
                <FileText className="w-8 h-8 text-[#004aad]" />
              </div>
              <h3 className="text-xl mb-4 text-gray-900">Apply to Join MusliFin</h3>
              <ul className="text-gray-600 space-y-2 text-left">
                <li>• Complete short application form</li>
                <li>• Compliance verification</li>
                <li>• License validation</li>
                <li>• Quick approval process</li>
              </ul>
            </Card>

            {/* Step 2 */}
            <Card className="p-8 text-center border-2 border-[#66afff] relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-[#66afff] rounded-full flex items-center justify-center">
                <span className="text-white text-sm">2</span>
              </div>
              <div className="w-16 h-16 bg-[#ccdfff] rounded-full flex items-center justify-center mx-auto mb-6 mt-4">
                <Monitor className="w-8 h-8 text-[#004aad]" />
              </div>
              <h3 className="text-xl mb-4 text-gray-900">Clients Apply on Our Website</h3>
              <ul className="text-gray-600 space-y-2 text-left">
                <li>• MusliFin handles all applications</li>
                <li>• Complete credit checks</li>
                <li>• Documentation processing</li>
                <li>• Zero dealership involvement</li>
              </ul>
            </Card>

            {/* Step 3 */}
            <Card className="p-8 text-center border-2 border-[#99c7ff] relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-[#99c7ff] rounded-full flex items-center justify-center">
                <span className="text-white text-sm">3</span>
              </div>
              <div className="w-16 h-16 bg-[#ccdfff] rounded-full flex items-center justify-center mx-auto mb-6 mt-4">
                <Package className="w-8 h-8 text-[#004aad]" />
              </div>
              <h3 className="text-xl mb-4 text-gray-900">Receive Ready-to-Sign Package</h3>
              <ul className="text-gray-600 space-y-2 text-left">
                <li>• Pre-qualified customer details</li>
                <li>• Finalized pricing &amp; terms</li>
                <li>• Complete contract structure</li>
                <li>• All documents prepared</li>
              </ul>
              <div className="mt-4 p-3 bg-[#e6f2ff] rounded-lg">
                <h4 className="text-sm text-gray-900 mb-2">Your Responsibilities:</h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• Verify ID and insurance</li>
                  <li>• In-person contract signing</li>
                  <li>• Hand over the vehicle</li>
                </ul>
              </div>
            </Card>

            {/* Step 4 */}
            <Card className="p-8 text-center border-2 border-[#ccdfff] relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-[#ccdfff] rounded-full flex items-center justify-center">
                <span className="text-[#004aad] text-sm">4</span>
              </div>
              <div className="w-16 h-16 bg-[#ccdfff] rounded-full flex items-center justify-center mx-auto mb-6 mt-4">
                <DollarSign className="w-8 h-8 text-[#004aad]" />
              </div>
              <h3 className="text-xl mb-4 text-gray-900">You Take Over — Just Like Any Traditional Deal</h3>
              <ul className="text-gray-600 space-y-2 text-left">
                <li>• After signing and bank account info collection</li>
                <li>• Dealership begins collecting monthly payments directly</li>
                <li>• Client becomes your regular financing customer</li>
                <li>• MusliFin exits the process entirely</li>
              </ul>
              <div className="mt-4 p-3 bg-[#e6f2ff] rounded-lg">
                <p className="text-xs text-gray-600">
                  <strong>Coming 2026:</strong> MusliServe will automate collections, support, and reporting on your behalf.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Dealer Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl mb-6 text-gray-900">Dealer Benefits</h2>
            <p className="text-xl text-gray-600">Why partnering with MusliFin makes business sense</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="p-6 text-center hover:shadow-lg transition-shadow bg-white">
              <CheckCircle className="w-12 h-12 text-[#004aad] mx-auto mb-4" />
              <h3 className="text-lg mb-3 text-gray-900">No Staff Training Required</h3>
              <p className="text-gray-600 text-sm">
                MusliFin handles all customer interactions, credit checks, and documentation. Your team focuses on what they do best.
              </p>
            </Card>
            
            <Card className="p-6 text-center hover:shadow-lg transition-shadow bg-white">
              <Zap className="w-12 h-12 text-[#004aad] mx-auto mb-4" />
              <h3 className="text-lg mb-3 text-gray-900">Instant Contract Delivery</h3>
              <p className="text-gray-600 text-sm">
                Receive fully processed applications with completed contracts. No waiting, no back-and-forth.
              </p>
            </Card>
            
            <Card className="p-6 text-center hover:shadow-lg transition-shadow bg-white">
              <Users className="w-12 h-12 text-[#004aad] mx-auto mb-4" />
              <h3 className="text-lg mb-3 text-gray-900">Serve Growing Faith-Based Segment</h3>
              <p className="text-gray-600 text-sm">
                Tap into the 5M+ North American Muslim market with authentic, Shariah-compliant financing options.
              </p>
            </Card>
            
            <Card className="p-6 text-center hover:shadow-lg transition-shadow bg-white">
              <HeadphonesIcon className="w-12 h-12 text-[#004aad] mx-auto mb-4" />
              <h3 className="text-lg mb-3 text-gray-900">Upcoming Support Platform</h3>
              <p className="text-gray-600 text-sm">
                MusliServe (2026) will provide backend automation for collections and compliance management.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Process Details Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl mb-6 text-gray-900">
                Just List Your Cars, We Handle Everything Else
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                MusliFin takes care of sales, customer sourcing, and the entire financing process. You just need to publish your cars on Sayaara.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Users className="text-[#004aad] w-5 h-5 mt-1" />
                  <div>
                    <h4 className="text-gray-900 mb-1">Customer Sourcing & Sales</h4>
                    <p className="text-gray-600 text-sm">We source qualified customers and handle the entire sales process. Pre-screened buyers come to you ready to purchase.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <ShieldCheck className="text-[#004aad] w-5 h-5 mt-1" />
                  <div>
                    <h4 className="text-gray-900 mb-1">Complete Finance Management</h4>
                    <p className="text-gray-600 text-sm">All Shariah-compliant financing, credit approval, and compliance handled before customers reach your dealership.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Monitor className="text-[#004aad] w-5 h-5 mt-1" />
                  <div>
                    <h4 className="text-gray-900 mb-1">Simple Sayaara Integration</h4>
                    <p className="text-gray-600 text-sm">Just publish your inventory on our Sayaara platform. We handle marketing, customer acquisition, and deal closing.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-8 rounded-2xl">
              <h3 className="text-xl mb-6 text-gray-900 text-center">Partnership Benefits</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Setup Time</span>
                  <span className="text-[#004aad]">&lt; 1 Week</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Staff Training Required</span>
                  <span className="text-[#004aad]">None</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Application Processing</span>
                  <span className="text-[#004aad]">Fully Automated</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Customer Segment</span>
                  <span className="text-[#004aad]">5M+ Muslims</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Your Overhead</span>
                  <span className="text-[#004aad]">Zero</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Apply Now Form Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ApplyNowForm 
            className="bg-gray-50 p-8 rounded-2xl border border-gray-200"
            defaultInterest="Learning more about MusliFin as a Dealership"
          />
        </div>
      </section>

      {/* MusliServe Preview Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl mb-6 text-gray-900">Coming Soon: MusliServe</h2>
            <p className="text-xl text-gray-600">Enhanced backend automation for partner dealerships</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6 text-center bg-white border-2 border-[#ccdfff]">
              <div className="w-12 h-12 bg-[#ccdfff] rounded-full flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-6 h-6 text-[#004aad]" />
              </div>
              <h3 className="text-lg mb-3 text-gray-900">Payment Collection</h3>
              <p className="text-gray-600 text-sm">
                Automated monthly payment processing and collection management.
              </p>
            </Card>
            
            <Card className="p-6 text-center bg-white border-2 border-[#ccdfff]">
              <div className="w-12 h-12 bg-[#ccdfff] rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-6 h-6 text-[#004aad]" />
              </div>
              <h3 className="text-lg mb-3 text-gray-900">Compliance Monitoring</h3>
              <p className="text-gray-600 text-sm">
                Real-time compliance tracking and Shariah audit support.
              </p>
            </Card>
            
            <Card className="p-6 text-center bg-white border-2 border-[#ccdfff]">
              <div className="w-12 h-12 bg-[#ccdfff] rounded-full flex items-center justify-center mx-auto mb-4">
                <HeadphonesIcon className="w-6 h-6 text-[#004aad]" />
              </div>
              <h3 className="text-lg mb-3 text-gray-900">Customer Support</h3>
              <p className="text-gray-600 text-sm">
                Dedicated customer service for financing-related inquiries.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-[#004aad]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl mb-6 text-white">
            Join North America's Trusted Platform for Halal Auto Finance
          </h2>
          <p className="text-xl text-[#ccdfff] mb-8">
            Partner with MusliFin today and start serving the growing Muslim customer base with zero additional overhead.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <ApplyNowForm 
              asModal={true}
              defaultInterest="Becoming a Partner"
              triggerButton={
                <Button 
                  size="lg" 
                  className="bg-white text-[#004aad] hover:bg-gray-100 px-8 py-4 text-lg"
                >
                  Apply to Become a Partner Dealership
                </Button>
              }
            />
            <Button 
              size="lg" 
              variant="outline" 
              onClick={() => setCurrentPage('investors')}
              className="border-white !text-white hover:bg-[#0056cc] hover:border-[#0056cc] !bg-transparent px-8 py-4 text-lg"
            >
              Learn About Investment Opportunities
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}